import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import FilterBar from '../components/ui/FilterBar';
import Pagination from '../components/ui/Pagination';
import { useDebounce } from '../hooks/useDebounce';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { formatDate } from '../lib/utils';
import ContentRequestCreateModal from '../components/modals/ContentRequestCreateModal';
import type {
  ContentRequest,
  ContentRequestStats,
  ContentRequestStatus,
  ContentRequestPriority
} from '../types/contentRequest';
import {
  Clapperboard,
  Plus,
  ThumbsUp,
  Bell,
  Film,
  Tv,
  Trash2
} from 'lucide-react';

const STATUS_LABEL: Record<ContentRequestStatus, string> = {
  open: 'Aberto',
  in_progress: 'Em Andamento',
  added: 'Adicionado',
  rejected: 'Recusado',
  duplicate: 'Duplicado'
};

const PRIORITY_LABEL: Record<ContentRequestPriority, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta'
};

const statusBadgeClass = (s: ContentRequestStatus) => {
  switch (s) {
    case 'in_progress': return 'bg-blue-100 text-blue-800';
    case 'added': return 'bg-emerald-100 text-emerald-800';
    case 'rejected': return 'bg-red-100 text-red-800';
    case 'duplicate': return 'bg-slate-100 text-slate-700';
    default: return 'bg-amber-100 text-amber-800';
  }
};

const ContentRequests: React.FC = () => {
  const { canViewContentRequests, user } = useAuth();
  // Booleans estáveis — NÃO pôr funções de useAuth nas deps (recriam a cada render → loop infinito)
  const canView = canViewContentRequests();
  const isAdminUser = Number(user?.member_group_id) === 1;
  const myUsername = String(user?.username || '').trim().toLowerCase();
  const [items, setItems] = useState<ContentRequest[]>([]);
  const [stats, setStats] = useState<ContentRequestStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const debouncedSearch = useDebounce(searchTerm, 400);

  /** Só admin (grupo 1) ou o revendedor que abriu o pedido (username). */
  const canManageItem = (item: ContentRequest) => {
    if (isAdminUser) return true;
    const authorName = String(item.requester?.username || '').trim().toLowerCase();
    return !!(myUsername && authorName && myUsername === authorName);
  };

  const load = useCallback(async () => {
    if (!canView) return;
    try {
      setIsLoading(true);
      setError('');
      const params: Record<string, string> = {
        page: String(currentPage),
        limit: '15'
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;

      const [listRes, statsRes] = await Promise.all([
        apiService.getContentRequests(params),
        apiService.getContentRequestStats()
      ]);
      setItems(listRes.data || []);
      setTotalPages(listRes.pagination?.pages || 1);
      setStats(statsRes.data || null);
    } catch (err: any) {
      const status = err?.response?.status;
      const msg =
        status === 429
          ? 'Demasiados pedidos — aguarda uns segundos e actualiza a página.'
          : err?.response?.data?.error || err?.message || 'Falha ao carregar solicitações';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [canView, currentPage, debouncedSearch, statusFilter, typeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const patchItem = (updated: ContentRequest) => {
    setItems((prev) => prev.map((x) => (x._id === updated._id ? updated : x)));
  };

  const onVote = async (id: string) => {
    try {
      const rid = String(id);
      setBusyId(rid);
      const res = await apiService.voteContentRequest(rid);
      if (res.data) patchItem(res.data);
      const st = await apiService.getContentRequestStats();
      setStats(st.data || null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao votar');
    } finally {
      setBusyId(null);
    }
  };

  const onWatch = async (id: string) => {
    try {
      const rid = String(id);
      setBusyId(rid);
      const res = await apiService.watchContentRequest(rid);
      if (res.data) patchItem(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao acompanhar');
    } finally {
      setBusyId(null);
    }
  };

  const onStatus = async (id: string, status: ContentRequestStatus) => {
    const item = items.find((x) => x._id === id);
    if (item && !canManageItem(item)) {
      setError('Sem permissão para alterar este pedido');
      return;
    }
    try {
      const rid = String(id);
      setBusyId(rid);
      const res = await apiService.updateContentRequestStatus(rid, status);
      if (res.data) patchItem(res.data);
      const st = await apiService.getContentRequestStats();
      setStats(st.data || null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao actualizar status');
    } finally {
      setBusyId(null);
    }
  };

  const onPriority = async (id: string, priority: ContentRequestPriority) => {
    const item = items.find((x) => x._id === id);
    if (item && !canManageItem(item)) {
      setError('Sem permissão para alterar este pedido');
      return;
    }
    try {
      const rid = String(id);
      setBusyId(rid);
      const res = await apiService.updateContentRequestPriority(rid, priority);
      if (res.data) patchItem(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao actualizar prioridade');
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (id: string) => {
    const item = items.find((x) => x._id === id);
    if (item && !canManageItem(item)) {
      setError('Sem permissão para remover este pedido');
      return;
    }
    if (!window.confirm('Remover esta solicitação?')) return;
    try {
      const rid = String(id);
      setBusyId(rid);
      await apiService.deleteContentRequest(rid);
      setItems((prev) => prev.filter((x) => x._id !== rid));
      const st = await apiService.getContentRequestStats();
      setStats(st.data || null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao apagar solicitação');
    } finally {
      setBusyId(null);
    }
  };

  if (!canView) {
    return (
      <div className="p-6">
        <p className="text-slate-600">Sem permissão para ver solicitações.</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Clapperboard className="h-6 w-6 text-slate-700" />
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Solicitações</h1>
            <p className="text-sm text-slate-500">Pedidos de filmes e séries dos revendedores</p>
          </div>
          {stats && (
            <span className="ml-2 rounded-full bg-red-500 px-2 py-0.5 text-xs font-semibold text-white">
              {stats.active}
            </span>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          {!isAdminUser && stats?.quota && (
            <p className={`text-xs ${stats.quota.canCreate ? 'text-slate-500' : 'text-amber-700 font-medium'}`}>
              Seus pedidos na lista: {stats.quota.used}/{stats.quota.limit}
              {stats.quota.canCreate
                ? ` · restam ${stats.quota.remaining}`
                : ' · remova um para pedir de novo'}
            </p>
          )}
          <Button
            onClick={() => setCreateOpen(true)}
            disabled={!isAdminUser && !!stats?.quota && !stats.quota.canCreate}
            title={
              !isAdminUser && stats?.quota && !stats.quota.canCreate
                ? 'Limite de 3 pedidos na lista. Remova um dos seus para criar outro. Votos são livres.'
                : undefined
            }
          >
            <Plus className="h-4 w-4 mr-1" />
            Nova solicitação
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="xl:col-span-9 space-y-3">
          <FilterBar
            searchTerm={searchTerm}
            onSearchChange={(v) => {
              setSearchTerm(v);
              setCurrentPage(1);
            }}
            onSearchSubmit={() => setCurrentPage(1)}
            searchPlaceholder="Buscar por título, género, servidor, notas ou nome do revendedor..."
            statusFilter={statusFilter}
            onStatusFilterChange={(v) => {
              setStatusFilter(v);
              setCurrentPage(1);
            }}
            statusOptions={[
              { value: '', label: 'Todos os status' },
              { value: 'open', label: 'Aberto' },
              { value: 'in_progress', label: 'Em Andamento' },
              { value: 'added', label: 'Adicionado' },
              { value: 'rejected', label: 'Recusado' },
              { value: 'duplicate', label: 'Duplicado' }
            ]}
            packageFilter={typeFilter}
            onPackageFilterChange={(v) => {
              setTypeFilter(v);
              setCurrentPage(1);
            }}
            packageFilterLabel="Tipo"
            packageOptions={[
              { value: '', label: 'Todos os tipos' },
              { value: 'movie', label: 'Filme' },
              { value: 'series', label: 'Série' }
            ]}
            onClearFilters={() => {
              setSearchTerm('');
              setStatusFilter('');
              setTypeFilter('');
              setCurrentPage(1);
            }}
            showAdvancedFilters={true}
          />

          {isLoading ? (
            <div className="py-12 text-center text-slate-500">A carregar…</div>
          ) : items.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-slate-500">
                Nenhuma solicitação ainda. Crie a primeira.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <Card key={item._id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="flex-shrink-0">
                        {item.coverUrl ? (
                          <img
                            src={item.coverUrl}
                            alt=""
                            className="h-24 w-16 rounded object-cover bg-slate-100"
                          />
                        ) : (
                          <div className="h-24 w-16 rounded bg-slate-100 flex items-center justify-center text-slate-400">
                            {item.type === 'series' ? <Tv className="h-6 w-6" /> : <Film className="h-6 w-6" />}
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="text-base font-semibold text-slate-900 truncate">{item.title}</h3>
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                            {item.type === 'series' ? 'Séries' : 'Filmes'}
                          </span>
                          {item.type === 'series' && (item.season || item.episode) && (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                              {item.season ? `S${String(item.season).padStart(2, '0')}` : ''}
                              {item.episode ? `E${String(item.episode).padStart(2, '0')}` : ' · Todas as temporadas'}
                            </span>
                          )}
                          {item.genre && (
                            <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700">{item.genre}</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mb-2">
                          {item.serverLabel ? <>Servidor: {item.serverLabel} · </> : null}
                          Solicitado por <strong>{item.requester?.username}</strong>
                          {item.createdAt ? <> em {formatDate(item.createdAt)}</> : null}
                        </p>
                        {item.notes && (
                          <p className="text-sm text-slate-600 line-clamp-2 mb-2">{item.notes}</p>
                        )}

                        <div className="flex flex-wrap items-center gap-3">
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(item.status)}`}>
                            {STATUS_LABEL[item.status]}
                          </span>
                          <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                            <span className={`h-2 w-2 rounded-full ${item.priority === 'high' ? 'bg-red-500' : item.priority === 'low' ? 'bg-slate-400' : 'bg-amber-400'}`} />
                            {PRIORITY_LABEL[item.priority]}
                          </span>

                          {canManageItem(item) && (
                            <>
                              <select
                                className="text-xs border rounded px-2 py-1 bg-white"
                                value={item.status}
                                disabled={busyId === item._id}
                                title="Só o autor do pedido ou admin/sócio"
                                onChange={(e) => onStatus(item._id, e.target.value as ContentRequestStatus)}
                              >
                                {Object.entries(STATUS_LABEL).map(([k, v]) => (
                                  <option key={k} value={k}>{v}</option>
                                ))}
                              </select>
                              <select
                                className="text-xs border rounded px-2 py-1 bg-white"
                                value={item.priority}
                                disabled={busyId === item._id}
                                title="Só o autor do pedido ou admin/sócio"
                                onChange={(e) => onPriority(item._id, e.target.value as ContentRequestPriority)}
                              >
                                {Object.entries(PRIORITY_LABEL).map(([k, v]) => (
                                  <option key={k} value={k}>{v}</option>
                                ))}
                              </select>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="flex md:flex-col items-center md:items-end gap-2 flex-shrink-0">
                        <div className="text-sm text-slate-600 flex items-center gap-1">
                          <ThumbsUp className="h-4 w-4" />
                          {item.votes?.count || 0} votos
                        </div>
                        <Button
                          size="sm"
                          disabled={busyId === item._id}
                          onClick={() => onVote(item._id)}
                          className={
                            item.hasVoted
                              ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600 shadow-sm'
                              : 'border-slate-300 text-slate-700'
                          }
                          variant={item.hasVoted ? 'default' : 'outline'}
                        >
                          <ThumbsUp className={`h-3.5 w-3.5 mr-1 ${item.hasVoted ? 'fill-current' : ''}`} />
                          {item.hasVoted ? 'Votado' : 'Votar'}
                        </Button>
                        <Button
                          size="sm"
                          disabled={busyId === item._id}
                          onClick={() => onWatch(item._id)}
                          className={
                            item.isWatching
                              ? 'bg-amber-500 hover:bg-amber-600 text-white border-amber-500 shadow-sm'
                              : 'border-slate-300 text-slate-700'
                          }
                          variant={item.isWatching ? 'default' : 'outline'}
                        >
                          <Bell className={`h-3.5 w-3.5 mr-1 ${item.isWatching ? 'fill-current' : ''}`} />
                          {item.isWatching ? 'A acompanhar' : 'Acompanhar'}
                        </Button>
                        {canManageItem(item) && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyId === item._id}
                            onClick={() => onDelete(item._id)}
                            className="border-red-200 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            Remover
                          </Button>
                        )}
                        <span className="text-xs text-slate-400">{item.commentCount || 0} comentários</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>

        <div className="xl:col-span-3 space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Mais solicitados</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(stats?.topVoted || []).length === 0 && (
                <p className="text-sm text-slate-500">Sem dados ainda.</p>
              )}
              {(stats?.topVoted || []).map((t, i) => (
                <div key={t._id} className="flex items-center gap-2 text-sm">
                  <span className="w-5 text-slate-400 font-medium">{i + 1}</span>
                  {t.coverUrl ? (
                    <img src={t.coverUrl} alt="" className="h-8 w-6 rounded object-cover" />
                  ) : (
                    <div className="h-8 w-6 rounded bg-slate-100" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-slate-800">{t.title}</p>
                    <p className="text-xs text-slate-500">{t.votes} votos</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Actividade recente</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(stats?.recentActivity || []).length === 0 && (
                <p className="text-sm text-slate-500">Sem actividade.</p>
              )}
              {(stats?.recentActivity || []).map((a) => (
                <div key={a._id} className="flex gap-2 text-sm">
                  <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600 uppercase">
                    {(a.username || '?').slice(0, 2)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-slate-700">
                      <strong>{a.username}</strong> criou <span className="font-medium">{a.title}</span>
                    </p>
                    <p className="text-xs text-slate-400">{formatDate(a.createdAt)}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      <ContentRequestCreateModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCurrentPage(1);
          load();
        }}
      />
    </div>
  );
};

export default ContentRequests;
